import { Stack, Text } from "flicket-ui";
import { Icon } from "~components";
import CustomModal from "~components/CustomModal";
import { CompetitionType } from "~graphql/typed-document-nodes";
import { ClickOptions, useSocialShare } from "~hooks/useSocialShare";
import { useActiveCompetitionByType } from "../hooks/useActiveCompetitions";
import { PageContext } from "../hooks/useDetailsPageContext";
import { useSocialLinkOptions } from "../hooks/useSocialLinkOptions";
import useCaptureClick from "~telemetry/traces/useCaptureClick";
import { AttributeNames } from "~telemetry/enums/AttributeNames";
import { useOrganization } from "~hooks";
import { getVariant } from "../utils/socialLinkHelpers";
import { useReferralLink } from "../hooks/useReferralLink";
import {
  ExplicitPlatformActions,
  SocialUserInteractions,
} from "~features/competitions/helpers";
import { openExternalLink } from "~lib/helpers/openExternalLink";

export const RegistrationSuccessModalContent = ({
  pageContext,
}: {
  pageContext: PageContext;
}) => {
  const { organization } = useOrganization();

  const {
    waitlistUserId,
    event: { id: eventId, title: eventTitle },
    userEmail,
  } = pageContext;

  const captureClick = useCaptureClick({
    forwardToNest: true,
    eventId,
    attributes: {
      // todo: pass competitionId when implement competition
      // [AttributeNames.FLICKET_COMPETITION_ID]: competitionId,
      [AttributeNames.FLICKET_EVENT_PHASE]: "registration",
    },
  });

  const { activeCompetition } = useActiveCompetitionByType(
    CompetitionType.Registration,
    eventId
  );

  const variant = getVariant({
    waitlistUserId,
    activeCompetition,
  });

  const { shareOptions, socialOptions } = useSocialLinkOptions({
    eventId,
    variant,
    activeCompetition,
  });

  const link = useReferralLink({
    eventId,
    waitlistUserId,
    organizationSlug: organization.slug,
  });

  const {
    facebookHandler,
    twitterHandler,
    emailHandler,
    copyHandler,
  } = useSocialShare();

  const socialLinks: {
    icon: string;
    label: string;
    handler: (options: ClickOptions) => void;
    action: ExplicitPlatformActions | "link:copy";
    context: ClickOptions;
    analyticsEventName: SocialUserInteractions;
  }[] = [
    {
      label: "Post on Facebook",
      icon: "facebook",
      handler: facebookHandler,
      action: "facebook:share",
      context: {
        url: link,
        variant: "share",
      },
      analyticsEventName: "share-on-facebook",
    },
    {
      label: "Share on X",
      icon: "x",
      handler: twitterHandler,
      action: "twitter:share",
      context: {
        url: link,
        eventTitle,
        variant: "tweet",
      },
      analyticsEventName: "share-on-twitter",
    },
    {
      label: "Copy link",
      icon: "copy-link",
      action: "link:copy",
      handler: copyHandler,
      context: {
        url: link,
      },
      analyticsEventName: "copy-link",
    },
    {
      label: "Invite via Messenger",
      icon: "messenger",
      handler: facebookHandler,
      context: {
        url: link,
        variant: "direct-message",
      },
      analyticsEventName: "invite-on-messenger",
      action: "messenger:invite",
    },
    {
      label: "Invite via X",
      icon: "x",
      handler: twitterHandler,
      action: "twitter:invite",
      context: {
        url: link,
        eventTitle,
        variant: "direct-message",
      },
      analyticsEventName: "invite-on-twitter",
    },
    {
      label: "Like on Facebook",
      icon: "fb-like",
      handler: openExternalLink,
      context: { url: socialOptions?.facebook?.socialUrl },
      analyticsEventName: "like-on-facebook",
      action: "facebook:like",
    },
    {
      label: "Facebook RSVP",
      icon: "facebook-event",
      handler: openExternalLink,
      context: { url: socialOptions?.facebookEventPage?.socialUrl },
      analyticsEventName: "rsvp-facebook",
      action: "facebook:rsvp",
    },
    {
      label: "Follow on Instagram",
      icon: "instagram",
      handler: openExternalLink,
      context: { url: socialOptions?.instagram?.socialUrl },
      analyticsEventName: "follow-on-instagram",
      action: "instagram:follow",
    },
    {
      label: "Follow on Tik Tok",
      icon: "tiktok",
      handler: openExternalLink,
      context: { url: socialOptions?.tiktok?.socialUrl },
      analyticsEventName: "follow-on-tiktok",
      action: "tiktok:follow",
    },
    {
      label: "Share via Email",
      icon: "email",
      handler: emailHandler,
      action: "email:invite",
      analyticsEventName: "invite-via-email",
      context: {
        url: link,
        eventTitle,
      },
    },
  ];

  const shareOptionActions = shareOptions
    ? shareOptions.map((option) => option.promoterAction)
    : [];

  const socialOptionsActions = socialOptions
    ? Object.values(socialOptions)?.map((option) => option.promoterAction)
    : [];

  const allActions = [...shareOptionActions, ...socialOptionsActions];

  return (
    <>
      <CustomModal.Header>You&lsquo;re on the list 🎉</CustomModal.Header>
      <CustomModal.Content>
        <Stack direction={"vertical"} gap={4}>
          <Text variant="regular">
            We&lsquo;ll email you at {userEmail} as soon as presale tickets go
            on sale.
          </Text>
          <Text variant="header.S">Tell your friends you&lsquo;re going!</Text>
          <Stack flexWrap={"wrap"} gap={1}>
            {socialLinks
              .filter(
                (socialLink) =>
                  allActions.includes(socialLink.action) ||
                  socialLink.action === "link:copy"
              )
              .map((socialLink) => (
                <Stack
                  key={socialLink.label}
                  direction={"horizontal"}
                  gap={2}
                  flex={{ _: undefined, md: `calc(50% - 8px)` }}
                  alignItems={"center"}
                  paddingX={2}
                  paddingY={1}
                  cursor={"pointer"}
                  onClick={captureClick(
                    () => socialLink.handler(socialLink.context),
                    socialLink.analyticsEventName
                  )}
                >
                  <Icon icon={socialLink.icon} fontSize={40} />
                  <Text variant="regular">{socialLink.label}</Text>
                </Stack>
              ))}
          </Stack>
        </Stack>
      </CustomModal.Content>
    </>
  );
};
